// S1 body — the wizard's landing screen at `/onboarding` (Module 02 §4), UX-1
// register. Inside the shell (which draws the page head and the ONE Stepper)
// this screen answers "what do I do next": the resume card with its single
// primary action, and the completeness card with the go-live control.
//
// §4 S1 empty state: "brand-new tenant sees step 1 auto-opened" — a tenant
// whose session is still `draft` with no step touched is sent straight to step
// 1 once, on first load. The Stepper brings them back here; the redirect never
// repeats within a tab (sessionStorage guard).

import { useEffect, useRef } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import { Card } from "../../../components/card";
import { CardSkeleton, ErrorState } from "../../../components/states";
import { requestIdOf } from "../../../lib/request";
import { GoLiveButton } from "../components/go-live-button";
import { ScoreDial } from "../components/score-dial";
import { useOnboardingSession } from "../hooks/onboarding-queries";
import { GO_LIVE_SCORE_THRESHOLD, resumeTarget, allStepsComplete } from "../lib/wizard-steps";

const AUTO_OPEN_KEY = "assessly.onboarding.autoOpenedStep1";

function alreadyAutoOpened(): boolean {
  try {
    return window.sessionStorage.getItem(AUTO_OPEN_KEY) === "1";
  } catch {
    return false;
  }
}

function markAutoOpened(): void {
  try {
    window.sessionStorage.setItem(AUTO_OPEN_KEY, "1");
  } catch {
    // Storage disabled: the redirect then behaves as before (once per mount).
  }
}

export function WizardHomeScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = useOnboardingSession();
  const autoOpened = useRef(false);

  const data = session.data;
  const isBrandNew =
    data?.session.status === "draft" && data.steps.every((s) => s.status === "not_started");

  // §4 S1's empty state: a brand-new tenant is sent straight to step 1 rather
  // than shown a page with nothing on it — ONCE, per tab (sessionStorage), so a
  // user who navigates back here can read the S1 body.
  useEffect(() => {
    if (!isBrandNew || autoOpened.current) return;
    autoOpened.current = true;
    if (alreadyAutoOpened()) return;
    markAutoOpened();
    void navigate({ to: "/onboarding/vertical", replace: true });
  }, [isBrandNew, navigate]);

  if (session.isPending) {
    return <CardSkeleton lines={6} label={t("onboarding.shell.loading")} />;
  }
  if (session.error !== null || data === undefined) {
    return (
      <ErrorState
        title={t("onboarding.shell.errorTitle")}
        detail={t("onboarding.shell.errorDetail")}
        retryLabel={t("onboarding.shell.retry")}
        onRetry={() => void session.refetch()}
        requestId={requestIdOf(session.error)}
        requestIdLabel={t("onboarding.shell.requestId")}
      />
    );
  }

  const { session: row, steps, checklist_summary: summary } = data;
  const resume = resumeTarget(row, steps);
  const done = allStepsComplete(steps);

  return (
    <div className="wizard-home">
      {/* The `home.empty*` copy: a returning admin who navigates back to
          /onboarding on an untouched session sees the "let's set up your
          factory" state instead of a bare resume card. */}
      {isBrandNew ? (
        <Card className="wizard-home-empty" data-testid="wizard-home-empty">
          <div className="wizard-home-empty-body">
            <p className="wizard-home-empty-title">{t("onboarding.home.emptyTitle")}</p>
            <p className="wizard-step-blurb">{t("onboarding.home.emptyBody")}</p>
            <Button
              variant="primary"
              onClick={() => {
                void navigate({ to: "/onboarding/vertical" });
              }}
            >
              {t("onboarding.home.emptyCta")}
            </Button>
          </div>
        </Card>
      ) : (
        <Card title={t("onboarding.home.resumeTitle")} className="wizard-home-resume">
          <div className="wizard-home-resume-body">
            {done ? <p className="wizard-step-blurb">{t("onboarding.home.allStepsDone")}</p> : null}
            {resume.kind === "step" ? (
              <Button
                variant="primary"
                className="wizard-resume-cta"
                onClick={() => {
                  void navigate({ to: resume.to });
                }}
              >
                {t("onboarding.home.resumeCta", { number: resume.stepNo })}
              </Button>
            ) : resume.kind === "checklist" ? (
              <Button
                variant="primary"
                className="wizard-resume-cta"
                onClick={() => {
                  void navigate({ to: resume.to });
                }}
              >
                {t("onboarding.home.resumeChecklistCta")}
              </Button>
            ) : (
              <p className="wizard-step-blurb">{t("onboarding.steps.unavailableHint")}</p>
            )}
          </div>
        </Card>
      )}

      <Card title={t("onboarding.home.scoreHeading")} className="wizard-home-score">
        <div className="wizard-home-score-body">
          <ScoreDial
            score={summary.score}
            threshold={GO_LIVE_SCORE_THRESHOLD}
            blockingUnmet={summary.blocking_unmet.length}
            neverEvaluated={summary.score === 0 && summary.blocking_unmet.length > 0}
          />
          <Link to="/onboarding/checklist" className="ds-lnk">
            {t("onboarding.home.checklistLink")}
          </Link>
          <GoLiveButton session={row} steps={steps} summary={summary} primary={false} />
        </div>
      </Card>
    </div>
  );
}
